/* eslint-disable no-param-reassign */
import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { useField } from '@unform/core';
import { CustomInput, Error, RadioBox, Container, Option } from './styles';

interface InputRadioProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  isValid?: boolean;
  className?: string;
  options: {
    id: string;
    value: string;
    option: string | JSX.Element;
  }[];
  onChangeFireCallback?: () => void;
}

const InputRadio: React.FC<InputRadioProps> = ({
  name,
  type,
  disabled = false,
  options,
  onChangeFireCallback,
  ...rest
}) => {
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const { fieldName, defaultValue, registerField, error, clearError } =
    useField(name);

  const handleOnChange = useCallback(() => {
    onChangeFireCallback?.();
    clearError();
  }, [onChangeFireCallback, clearError]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRefs,
      getValue: (refs: typeof inputRefs) => {
        return refs.current?.find((ref) => ref?.checked)?.value || '';
      },
      setValue: (refs: typeof inputRefs, id: string) => {
        const inputRef = refs.current?.find((ref) => ref?.id === id);
        if (inputRef) inputRef.checked = true;
      },
      clearValue: (refs: typeof inputRefs) => {
        const inputRef = refs.current?.find((ref) => ref?.checked === true);
        if (inputRef) inputRef.checked = false;
      },
    });
  }, [fieldName, registerField, type]);

  return (
    <Container>
      {options.map((option) => (
        <Option key={option.id}>
          <CustomInput
            ref={(ref) => inputRefs.current.push(ref as HTMLInputElement)}
            type="radio"
            name={name}
            defaultChecked={defaultValue?.includes?.(option.id) ?? false}
            value={option.value}
            id={option.id}
            disabled={disabled}
            onClick={handleOnChange}
            {...rest}
          />
          <RadioBox isError={!!error} />
          <p>{option.option}</p>
        </Option>
      ))}

      {error && (
        <Error>
          <span dangerouslySetInnerHTML={{ __html: error }} />
        </Error>
      )}
    </Container>
  );
};

export default InputRadio;
